import {
  CoreFieldMaps,
  AdditionalField,
  ParseResult,
  Transform,
  TransformMap,
} from './types';
import { maskStrings, splitJqPath } from './jqPaths';
import { emitsUnquoted, parseJqLiteral } from './transforms';
import {
  JQ_LITERAL_SRC,
  isStrictPath,
  parseConditions,
  parseTransformExpression,
} from './parseExpressions';

// jq → builder state: tokenize the output object and rebuild the builder from it.

// ── Body tokenizer + field-expression classifier ──
//
// The output object is tokenized ONCE into top-level `key: <expr>` pairs (string- and
// nesting-aware), and each expression goes through a single classification ladder.
// This replaces the previous whole-body regex scans, which matched keys and paths inside
// nested objects and string literals — corrupting value-map/default arguments and
// injecting spurious fields on regeneration.

type TopLevelField = { key: string; expr: string };

/** Extracts a balanced expression starting at `str[from]`; returns the expression and the index after it. */
function extractExpressionAt(
  str: string,
  from: number,
): { expr: string; end: number } {
  let depth = 0;
  let i = from;
  for (; i < str.length; i++) {
    const ch = str[i];
    if (ch === '"') {
      i++;
      while (i < str.length && str[i] !== '"') {
        if (str[i] === '\\') i++;
        i++;
      }
      continue;
    }
    if (ch === '(' || ch === '{' || ch === '[') depth++;
    else if (ch === ')' || ch === '}' || ch === ']') {
      if (depth === 0) break; // closing brace of parent object
      depth--;
    } else if (ch === ',' && depth === 0) break;
  }
  return { expr: str.slice(from, i).trim(), end: i };
}

/** Tokenizes a `{ ... }` object body into its top-level key/expression pairs.
 *  `clean` is false when any chunk could not be parsed as `key: expr` — callers must
 *  treat the filter as non-round-trippable rather than regenerate from a partial read. */
function extractTopLevelFields(objBody: string): {
  fields: TopLevelField[];
  clean: boolean;
} {
  const trimmed = objBody.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}'))
    return { fields: [], clean: false };
  const inner = trimmed.slice(1, -1);

  const fields: TopLevelField[] = [];
  let clean = true;
  let i = 0;
  while (i < inner.length) {
    while (i < inner.length && /[\s,]/.test(inner[i])) i++;
    if (i >= inner.length) break;

    let key: string | null = null;
    if (inner[i] === '"') {
      let closeQuote = i + 1;
      while (closeQuote < inner.length && inner[closeQuote] !== '"') {
        if (inner[closeQuote] === '\\') closeQuote++;
        closeQuote++;
      }
      if (closeQuote < inner.length) {
        try {
          key = JSON.parse(inner.slice(i, closeQuote + 1));
        } catch {
          key = inner.slice(i + 1, closeQuote);
        }
        i = closeQuote + 1;
      }
    } else {
      const identMatch = inner.slice(i).match(/^[A-Za-z_]\w*/);
      if (identMatch) {
        key = identMatch[0];
        i += identMatch[0].length;
      }
    }
    if (key === null) {
      clean = false;
      i = extractExpressionAt(inner, i).end + 1;
      continue;
    }

    while (i < inner.length && /\s/.test(inner[i])) i++;
    if (inner[i] !== ':') {
      clean = false;
      i = extractExpressionAt(inner, i).end + 1;
      continue;
    }
    i++;

    const { expr, end } = extractExpressionAt(inner, i);
    i = end;
    if (expr) fields.push({ key, expr });
    else clean = false;
  }
  return { fields, clean };
}

type FieldExprClass =
  | { kind: 'path'; path: string[] }
  | { kind: 'literal'; value: string }
  | { kind: 'transform'; path: string[] | null; transforms: Transform[] }
  | { kind: 'object'; body: string }
  | { kind: 'complex' };

const JQ_LITERAL_ONLY_RE = new RegExp(`^(?:${JQ_LITERAL_SRC})$`);

/** Classifies a field expression through a single ladder — the one place that decides
 *  whether an expression is builder-representable. */
function classifyFieldExpression(expr: string): FieldExprClass {
  const t = expr.trim();
  if (t.startsWith('{') && t.endsWith('}')) return { kind: 'object', body: t };
  // Bare literals (strings, numbers, booleans) — unmappedFieldExpr emits these for a
  // single-default field, and legacy literal-input configs are plain strings.
  if (JQ_LITERAL_ONLY_RE.test(t)) {
    const value = parseJqLiteral(t);
    // A quoted "123" / "false" would be re-emitted bare — a different type.
    if (t.startsWith('"') && emitsUnquoted(value)) return { kind: 'complex' };
    return { kind: 'literal', value };
  }
  if (isStrictPath(t)) return { kind: 'path', path: splitJqPath(t) };
  const transform = parseTransformExpression(t);
  if (transform)
    return {
      kind: 'transform',
      path: transform.path,
      transforms: transform.transforms,
    };
  return { kind: 'complex' };
}

/** Checks if the jq string has structural complexity beyond what the builder supports.
 *  Runs on string-masked text so user data (a condition value containing `as $x`) can't
 *  trip the checks. Pipe preambles/trailers are detected separately via the output-object
 *  offsets in parseJqToBuilderState. */
function hasStructuralComplexity(jq: string): boolean {
  const masked = maskStrings(jq);
  if (/\bas\s+\$\w+/.test(masked)) return true; // variable bindings
  if (/\bdef\s+\w+/.test(masked)) return true; // function definitions
  if (/\|\s*\.\w+\s*=[^=]/.test(masked)) return true; // pipe-assignment (.x = expr)
  return false;
}

/** Finds the opening `{` that matches the last `}` in the string, respecting nesting. */
/** Finds the outermost `{ ... }` block by scanning backwards from the last `}` to its matching `{`. */
function findOutputObjectRange(
  jqBody: string,
): { start: number; end: number } | null {
  const closingBraceIndex = jqBody.lastIndexOf('}');
  if (closingBraceIndex === -1) return null;

  let braceDepth = 0;
  for (let cursor = closingBraceIndex; cursor >= 0; cursor--) {
    const char = jqBody[cursor];
    if (char === '}') braceDepth++;
    else if (char === '{') {
      braceDepth--;
      if (braceDepth === 0) {
        return { start: cursor, end: closingBraceIndex + 1 };
      }
    }
  }
  return null;
}

/**
 * Parses an existing jq filter string back into builder state.
 * Returns `null` only for empty input. For complex/unparseable jq, returns a result
 * with `isFullyParseable: false` and populated `complexFields`.
 */
export function parseJqToBuilderState(jq: string): ParseResult | null {
  try {
    const trimmed = jq.trim();
    if (!trimmed) return null;

    const structurallyComplex = hasStructuralComplexity(trimmed);

    // Separate condition wrapper from body
    let conditionString = '';
    let body = trimmed;

    // Pattern 1: if (conds) then { ... } else empty end
    const ifMatch = trimmed.match(
      /^if\s*\(\s*([\s\S]+?)\s*\)\s*then\s*([\s\S]+?)\s*else\s*empty\s*end\s*$/,
    );
    if (ifMatch) {
      conditionString = ifMatch[1];
      body = ifMatch[2].trim();
    }

    // Pattern 2: select(conds) | { ... }
    if (!conditionString && /^\s*select\s*\(/.test(body)) {
      let depth = 1;
      const selectStart = body.indexOf('select(') + 7;
      let closeParen = -1;
      for (let i = selectStart; i < body.length; i++) {
        if (body[i] === '(') depth++;
        else if (body[i] === ')') {
          depth--;
          if (depth === 0) {
            closeParen = i;
            break;
          }
        }
      }
      if (closeParen !== -1) {
        const afterSelect = body.slice(closeParen + 1).trim();
        if (afterSelect.startsWith('|')) {
          conditionString = body.slice(selectStart, closeParen);
          body = afterSelect.slice(1).trim();
        }
      }
    }

    // Find the outermost output object `{ ... }` by matching the last `}` with its `{`
    const range = findOutputObjectRange(body);
    if (!range) {
      return {
        maps: {},
        conds: [],
        addl: [],
        complexFields: {},
        transforms: {},
        isFullyParseable: false,
        rawJq: trimmed,
      };
    }
    const outputBody = body.slice(range.start, range.end);
    // A pipe preamble before the object, or trailing content after it, means the filter
    // does more than the builder can represent.
    const hasBodyPreamble =
      range.start > 0 || body.slice(range.end).trim().length > 0;

    const parsedConds = conditionString
      ? parseConditions(conditionString)
      : { conds: [], unrecognized: [] };
    const conds = parsedConds.conds;

    const maps: CoreFieldMaps = {};
    const transforms: TransformMap = {};
    const addl: AdditionalField[] = [];
    const complexFields: Record<string, string> = {};

    const { fields: topFields, clean: bodyClean } =
      extractTopLevelFields(outputBody);
    let nestedClean = true;
    // Assigning a `__proto__` key on the plain objects below swaps their prototype
    // instead of storing the field, so it would vanish on re-save — keep it in Code mode.
    let hasProtoKey = false;

    for (const { key, expr } of topFields) {
      if (key === '__proto__') {
        hasProtoKey = true;
        continue;
      }
      // Nested location object → classify each axis independently.
      if (key === 'location') {
        const cls = classifyFieldExpression(expr);
        if (cls.kind !== 'object') {
          complexFields[key] = expr;
          continue;
        }
        const { fields: locFields, clean: locClean } = extractTopLevelFields(
          cls.body,
        );
        if (!locClean) {
          complexFields[key] = expr;
          nestedClean = false;
          continue;
        }
        for (const { key: axis, expr: axisExpr } of locFields) {
          const target = `location.${axis}`;
          if (axis !== 'lat' && axis !== 'lon') {
            complexFields[target] = axisExpr;
            continue;
          }
          if (axisExpr === 'null') continue; // genJq emits null for an unmapped axis
          const axisCls = classifyFieldExpression(axisExpr);
          if (axisCls.kind === 'path') maps[target] = axisCls.path;
          else if (axisCls.kind === 'transform') {
            if (axisCls.path) maps[target] = axisCls.path;
            if (axisCls.transforms.length)
              transforms[target] = axisCls.transforms;
          } else if (axisCls.kind === 'literal') {
            transforms[target] = [{ kind: 'default', arg: axisCls.value }];
          } else complexFields[target] = axisExpr;
        }
        continue;
      }

      // Additional / event_details bag → each entry is a user-named field.
      if (key === 'additional' || key === 'event_details') {
        const cls = classifyFieldExpression(expr);
        if (cls.kind !== 'object') {
          complexFields[key] = expr;
          continue;
        }
        const { fields: addlFields, clean: addlClean } = extractTopLevelFields(
          cls.body,
        );
        if (!addlClean) {
          complexFields[key] = expr;
          nestedClean = false;
          continue;
        }
        for (const { key: name, expr: fieldExpr } of addlFields) {
          const fieldCls = classifyFieldExpression(fieldExpr);
          if (fieldCls.kind === 'path')
            addl.push({ name, path: fieldCls.path.join('.') });
          else if (fieldCls.kind === 'transform') {
            // A path-less (null-base) chain is a path-less additional field.
            addl.push({
              name,
              path: fieldCls.path ? fieldCls.path.join('.') : '',
              transforms: fieldCls.transforms.length
                ? fieldCls.transforms
                : undefined,
            });
          } else if (fieldCls.kind === 'literal') {
            // A bare literal collapses to a single default pill on a path-less field.
            addl.push({
              name,
              path: '',
              transforms: [{ kind: 'default', arg: fieldCls.value }],
            });
          } else complexFields[`addl:${name}`] = fieldExpr;
        }
        continue;
      }

      const cls = classifyFieldExpression(expr);
      // A bare literal (legacy literal-input output, or unmappedFieldExpr's collapsed
      // single-default) becomes an unmapped field with a "Default if empty" pill.
      if (cls.kind === 'literal')
        transforms[key] = [{ kind: 'default', arg: cls.value }];
      else if (cls.kind === 'path') maps[key] = cls.path;
      else if (cls.kind === 'transform') {
        if (cls.path) maps[key] = cls.path;
        if (cls.transforms.length) transforms[key] = cls.transforms;
      } else complexFields[key] = expr;
    }

    const isFullyParseable =
      !structurallyComplex &&
      bodyClean &&
      nestedClean &&
      !hasProtoKey &&
      Object.keys(complexFields).length === 0 &&
      parsedConds.unrecognized.length === 0 &&
      !hasBodyPreamble;

    return {
      maps,
      conds,
      addl,
      complexFields,
      transforms,
      isFullyParseable,
      rawJq: trimmed,
    };
  } catch {
    return {
      maps: {},
      conds: [],
      addl: [],
      complexFields: {},
      transforms: {},
      isFullyParseable: false,
      rawJq: jq.trim(),
    };
  }
}
